import { BaseScene } from './BaseScene';
import { AchievementManager, AchievementCategory, AchievementConfig, AchievementRarity } from '../systems/AchievementManager';
import { saveUserProfile } from '../entities/base/UserProfile';

export class AchievementScene extends BaseScene {
  private achievementManager: AchievementManager;
  private currentCategory: AchievementCategory | 'all' = 'all';
  private contentContainer: Phaser.GameObjects.Container;
  private scrollY: number = 0;
  private maxScrollY: number = 0;

  constructor() {
    super({ key: 'Achievement' });
  }

  override create(): void {
    super.create();
    this.addBackground();
    
    this.achievementManager = AchievementManager.getInstance();
    
    this.createHeader();
    this.createCategoryTabs();
    this.createContentContainer();
    this.updateContent();
    this.setupScrolling();
  }

  private createHeader(): void {
    const centerX = this.cameras.main.centerX;
    
    // Title
    this.add.text(centerX, 60, 'ACHIEVEMENTS', {
      fontSize: '36px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Completion stats
    const stats = this.achievementManager.getCompletionStats();
    this.add.text(centerX, 100, `${stats.completed}/${stats.total} Completed (${stats.percentage}%)`, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Progress bar
    const progressBarWidth = 300;
    const progressBg = this.add.rectangle(centerX, 125, progressBarWidth, 10, 0x333333);
    const progressFill = this.add.rectangle(
      centerX - progressBarWidth/2 + (stats.percentage * progressBarWidth / 200),
      125,
      (stats.percentage * progressBarWidth / 100),
      10,
      0x4CAF50
    ).setOrigin(0, 0.5);
    
    // Back button
    const backBtn = this.add.text(50, 50, '← BACK', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#666666',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0, 0.5)
    .setInteractive({ useHandCursor: true });
    
    backBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      this.scene.start('MainMenu');
    });
    
    backBtn.on('pointerover', () => {
      this.audioManager.playButtonHover();
      backBtn.setStyle({ backgroundColor: '#888888' });
    });
    
    backBtn.on('pointerout', () => {
      backBtn.setStyle({ backgroundColor: '#666666' });
    });
  }

  private createCategoryTabs(): void {
    const centerX = this.cameras.main.centerX;
    const tabY = 160;
    
    const categories = [
      { key: 'all', name: 'ALL', color: '#2196F3' },
      { key: 'survival', name: 'SURVIVAL', color: '#4CAF50' },
      { key: 'combat', name: 'COMBAT', color: '#f44336' },
      { key: 'progression', name: 'PROGRESS', color: '#FF9800' },
      { key: 'collection', name: 'COLLECT', color: '#9C27B0' },
      { key: 'special', name: 'SPECIAL', color: '#FFD700' },
    ];
    
    const tabWidth = 100;
    const totalWidth = categories.length * tabWidth;
    const startX = centerX - totalWidth / 2;
    
    categories.forEach((category, index) => {
      const isActive = this.currentCategory === category.key;
      const x = startX + index * tabWidth + tabWidth / 2;
      
      const tab = this.add.text(x, tabY, category.name, {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: isActive ? category.color : '#444444',
        padding: { x: 8, y: 6 },
        fontFamily: 'Arial',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
      
      tab.on('pointerdown', () => {
        this.audioManager.playButtonClick();
        this.currentCategory = category.key as any;
        this.scene.restart();
      });
      
      if (!isActive) {
        tab.on('pointerover', () => {
          this.audioManager.playButtonHover();
          tab.setStyle({ backgroundColor: '#666666' });
        });
        
        tab.on('pointerout', () => {
          tab.setStyle({ backgroundColor: '#444444' });
        });
      }
    });
  }

  private createContentContainer(): void {
    this.contentContainer = this.add.container(0, 200);
  }

  private updateContent(): void {
    this.contentContainer.removeAll(true);
    
    let achievements: AchievementConfig[];
    
    if (this.currentCategory === 'all') {
      achievements = this.achievementManager.getAllAchievements();
    } else {
      achievements = this.achievementManager.getAchievementsByCategory(this.currentCategory as AchievementCategory);
    }
    
    let yOffset = 0;
    const itemHeight = 120;
    
    achievements.forEach(achievement => {
      this.createAchievementItem(achievement, yOffset);
      yOffset += itemHeight;
    });
    
    // Calculate max scroll based on content height
    const contentHeight = achievements.length * itemHeight;
    const visibleHeight = this.cameras.main.height - 220; // Account for header and tabs
    this.maxScrollY = Math.max(0, contentHeight - visibleHeight);
  }

  private createAchievementItem(achievement: AchievementConfig, y: number): void {
    const centerX = this.cameras.main.centerX;
    const itemContainer = this.add.container(0, y);
    
    // Background
    const bgColor = achievement.isCompleted ? 0x2d5a2d : 0x2d2d2d;
    const bg = this.add.rectangle(centerX, 0, 700, 100, bgColor, 0.9)
      .setStrokeStyle(2, this.getRarityColor(achievement.rarity));
    itemContainer.add(bg);
    
    // Icon
    const icon = this.add.text(centerX - 300, 0, achievement.icon, {
      fontSize: '32px',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    itemContainer.add(icon);
    
    // Achievement name
    const nameColor = achievement.isCompleted ? '#4CAF50' : '#ffffff';
    const name = this.add.text(centerX - 250, -20, achievement.name, {
      fontSize: '18px',
      color: nameColor,
      fontFamily: 'Arial',
      fontStyle: achievement.isCompleted ? 'bold' : 'normal',
    }).setOrigin(0, 0.5);
    itemContainer.add(name);
    
    // Rarity badge
    const rarityText = achievement.rarity.toUpperCase();
    const rarity = this.add.text(centerX + 250, -30, rarityText, {
      fontSize: '12px',
      color: '#ffffff',
      backgroundColor: this.getRarityColor(achievement.rarity).toString(),
      padding: { x: 6, y: 3 },
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    itemContainer.add(rarity);
    
    // Description
    const description = this.add.text(centerX - 250, 0, achievement.description, {
      fontSize: '14px',
      color: '#cccccc',
      fontFamily: 'Arial',
      wordWrap: { width: 400 },
    }).setOrigin(0, 0.5);
    itemContainer.add(description);
    
    // Progress
    const progress = this.achievementManager.getAchievementProgress(achievement.id);
    let progressText = `${progress.current}/${progress.target}`;
    
    if (achievement.isCompleted) {
      progressText = '✓ COMPLETED';
      
      // Show completion date
      if (achievement.completedAt) {
        const date = new Date(achievement.completedAt).toLocaleDateString();
        const completedText = this.add.text(centerX - 250, 20, `Completed: ${date}`, {
          fontSize: '12px',
          color: '#4CAF50',
          fontFamily: 'Arial',
        }).setOrigin(0, 0.5);
        itemContainer.add(completedText);
      }
    } else {
      // Progress bar
      const progressBarWidth = 150;
      const progressBg = this.add.rectangle(centerX + 150, 10, progressBarWidth, 8, 0x444444);
      itemContainer.add(progressBg);
      
      const progressFill = this.add.rectangle(
        centerX + 150 - progressBarWidth/2 + (progress.percentage * progressBarWidth / 200),
        10,
        (progress.percentage * progressBarWidth / 100),
        8,
        0x4CAF50
      ).setOrigin(0, 0.5);
      itemContainer.add(progressFill);
      
      progressText += ` (${progress.percentage}%)`;
    }
    
    const progressDisplay = this.add.text(centerX + 150, -10, progressText, {
      fontSize: '14px',
      color: achievement.isCompleted ? '#4CAF50' : '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    itemContainer.add(progressDisplay);
    
    // Reward info
    const rewardText = this.getRewardText(achievement);
    if (rewardText) {
      const reward = this.add.text(centerX + 250, 20, rewardText, {
        fontSize: '12px',
        color: '#FFD700',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
      itemContainer.add(reward);
    }
    
    // Claim button (if completed but not claimed)
    if (achievement.isCompleted && !this.isRewardClaimed(achievement)) {
      const claimBtn = this.add.text(centerX + 250, 0, 'CLAIM!', {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#4CAF50',
        padding: { x: 10, y: 5 },
        fontFamily: 'Arial',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
      
      claimBtn.on('pointerdown', () => {
        this.claimAchievementReward(achievement);
      });
      
      claimBtn.on('pointerover', () => {
        this.audioManager.playButtonHover();
        claimBtn.setStyle({ backgroundColor: '#388E3C' });
      });
      
      claimBtn.on('pointerout', () => {
        claimBtn.setStyle({ backgroundColor: '#4CAF50' });
      });
      
      itemContainer.add(claimBtn);
    }
    
    this.contentContainer.add(itemContainer);
  }

  private getRarityColor(rarity: AchievementRarity): number {
    switch (rarity) {
      case AchievementRarity.COMMON: return 0x9E9E9E;
      case AchievementRarity.RARE: return 0x2196F3;
      case AchievementRarity.EPIC: return 0x9C27B0;
      case AchievementRarity.LEGENDARY: return 0xFF9800;
      default: return 0x9E9E9E;
    }
  }

  private getRewardText(achievement: AchievementConfig): string {
    const rewards: string[] = [];
    
    if (achievement.reward.gold) {
      rewards.push(`${achievement.reward.gold}💰`);
    }
    
    if (achievement.reward.ruby) {
      rewards.push(`${achievement.reward.ruby}💎`);
    }
    
    if (achievement.reward.equipment) {
      rewards.push('⚔️ Equipment');
    }
    
    if (achievement.reward.title) {
      rewards.push(`📜 "${achievement.reward.title}"`);
    }
    
    return rewards.join(' + ');
  }

  private isRewardClaimed(achievement: AchievementConfig): boolean {
    // Check if reward has been claimed (simplified - in real game this would be more sophisticated)
    const claimedRewards = JSON.parse(localStorage.getItem('claimedAchievements') || '[]');
    return claimedRewards.includes(achievement.id);
  }

  private claimAchievementReward(achievement: AchievementConfig): void {
    this.audioManager.playButtonClick();
    
    // Apply reward to user profile
    const userProfile = (window as any).userProfile;
    AchievementManager.applyAchievementReward(achievement.reward, userProfile);
    saveUserProfile(userProfile);
    
    // Mark as claimed
    const claimedRewards = JSON.parse(localStorage.getItem('claimedAchievements') || '[]');
    claimedRewards.push(achievement.id);
    localStorage.setItem('claimedAchievements', JSON.stringify(claimedRewards));
    
    // Show reward notification
    let rewardMsg = 'Achievement Reward Claimed!\n';
    if (achievement.reward.gold) rewardMsg += `+${achievement.reward.gold} Gold\n`;
    if (achievement.reward.ruby) rewardMsg += `+${achievement.reward.ruby} Ruby\n`;
    if (achievement.reward.equipment) rewardMsg += `+${achievement.reward.equipment}\n`;
    if (achievement.reward.title) rewardMsg += `Title: "${achievement.reward.title}"`;
    
    this.showNotification(rewardMsg.trim(), 3000);
    
    // Update UI
    this.updateContent();
  }

  private setupScrolling(): void {
    // Mouse wheel scrolling
    this.input.on('wheel', (pointer: any, gameObjects: any, deltaX: number, deltaY: number) => {
      const scrollSpeed = 50;
      this.scrollY = Phaser.Math.Clamp(this.scrollY + deltaY * scrollSpeed / 100, -this.maxScrollY, 0);
      this.contentContainer.setY(200 + this.scrollY);
    });
    
    // Touch/drag scrolling (simplified)
    let isDragging = false;
    let dragStartY = 0;
    let contentStartY = 0;
    
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      isDragging = true;
      dragStartY = pointer.y;
      contentStartY = this.scrollY;
    });
    
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (isDragging) {
        const deltaY = pointer.y - dragStartY;
        this.scrollY = Phaser.Math.Clamp(contentStartY + deltaY, -this.maxScrollY, 0);
        this.contentContainer.setY(200 + this.scrollY);
      }
    });
    
    this.input.on('pointerup', () => {
      isDragging = false;
    });
  }

  // Show newly completed achievements
  public showCompletedAchievements(achievements: AchievementConfig[]): void {
    if (achievements.length === 0) return;
    
    // Create achievement popup for each completed achievement
    achievements.forEach((achievement, index) => {
      this.time.delayedCall(index * 1000, () => {
        this.showAchievementPopup(achievement);
      });
    });
  }

  private showAchievementPopup(achievement: AchievementConfig): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    
    // Background overlay
    const overlay = this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0x000000, 0.7)
      .setOrigin(0)
      .setInteractive();
    
    // Achievement panel
    const panel = this.add.rectangle(centerX, centerY, 400, 200, 0x1a1a1a, 0.95)
      .setStrokeStyle(3, this.getRarityColor(achievement.rarity));
    
    // Achievement completed text
    this.add.text(centerX, centerY - 70, '🎉 ACHIEVEMENT UNLOCKED!', {
      fontSize: '20px',
      color: '#FFD700',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    // Icon and name
    this.add.text(centerX - 80, centerY - 30, achievement.icon, {
      fontSize: '32px',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
    
    this.add.text(centerX + 20, centerY - 30, achievement.name, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    
    // Description
    this.add.text(centerX, centerY + 10, achievement.description, {
      fontSize: '14px',
      color: '#cccccc',
      fontFamily: 'Arial',
      align: 'center',
      wordWrap: { width: 350 },
    }).setOrigin(0.5);
    
    // Reward
    const rewardText = this.getRewardText(achievement);
    if (rewardText) {
      this.add.text(centerX, centerY + 40, `Reward: ${rewardText}`, {
        fontSize: '14px',
        color: '#FFD700',
        fontFamily: 'Arial',
      }).setOrigin(0.5);
    }
    
    // Close button
    const closeBtn = this.add.text(centerX, centerY + 70, 'AWESOME!', {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: '#4CAF50',
      padding: { x: 15, y: 8 },
      fontFamily: 'Arial',
    })
    .setOrigin(0.5)
    .setInteractive({ useHandCursor: true });
    
    closeBtn.on('pointerdown', () => {
      this.audioManager.playButtonClick();
      overlay.destroy();
      panel.destroy();
      closeBtn.destroy();
      this.children.list.filter(child => 
        child instanceof Phaser.GameObjects.Text && 
        (child.text.includes('ACHIEVEMENT UNLOCKED') || child.text.includes(achievement.name))
      ).forEach(child => child.destroy());
    });
    
    // Auto-close after 5 seconds
    this.time.delayedCall(5000, () => {
      if (overlay.active) {
        closeBtn.emit('pointerdown');
      }
    });
  }
} 